/**
 * Files on the ticket: the person's own uploads, and (19 Oct) the files IT
 * added in Ivanti, on the ticket or on a public note (never an internal one).
 *
 * The bytes are never fetched through the SDK: `getTicketAttachmentDownload`
 * hands back a short-lived same-origin path, and the browser follows it. A file that has not cleared the malware scan is listed but not
 * downloadable — the server would refuse it anyway (409 `ATTACHMENT_NOT_CLEAN`).
 */

import { Download, Icon, Paperclip } from "@norican/icons";
import { Badge, Button, Typography } from "@norican/react";

import { EmptyStateBlock, RelativeTime } from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import { logger } from "@/lib/logger";

import type { TicketAttachment } from "../api/contract";
import { useAttachmentDownload } from "../api/queries";
import { formatBytes } from "../lib/ticket-display";
import { useFeedbackToast } from "../lib/use-feedback-toast";

export interface TicketAttachmentsProps {
  ticketId: string;
  attachments: TicketAttachment[];
}

export function TicketAttachments({ ticketId, attachments }: TicketAttachmentsProps) {
  const { t } = useT("tickets");
  const { locale } = useLanguage();
  const toast = useFeedbackToast();
  const download = useAttachmentDownload();

  if (attachments.length === 0) {
    return (
      <EmptyStateBlock
        size="sm"
        icon={<Icon icon={Paperclip} size="md" tone="accent" />}
        title={t("attachment.none")}
        description={null}
      />
    );
  }

  const handleDownload = async (attachment: TicketAttachment) => {
    try {
      const descriptor = await download.mutateAsync({
        ticketId,
        attachmentId: attachment.attachment_id,
      });
      logger.debug("[tickets] attachment download", attachment.attachment_id);
      window.open(descriptor.url_path, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.failure(error, t("attachment.downloadFailed"));
    }
  };

  return (
    <ul className="flex flex-col gap-3">
      {attachments.map((attachment) => {
        // `scan_status` is optional on the wire, and an unscanned file is not a
        // clean one — treat a missing verdict as still pending.
        const scanStatus = attachment.scan_status ?? "PENDING";
        return (
          <li
            key={attachment.attachment_id}
            className="border-border-subtle flex flex-wrap items-center justify-between gap-3 border-b pb-3 last:border-0 last:pb-0"
          >
            <div className="flex min-w-0 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <Icon icon={Paperclip} size="sm" />
                <span className="truncate">{attachment.filename}</span>
                {scanStatus === "CLEAN" ? null : (
                  <Badge
                    tone={scanStatus === "BLOCKED" ? "danger" : "warning"}
                    variant="subtle"
                    size="xs"
                  >
                    {t(`attachment.scan.${scanStatus}`)}
                  </Badge>
                )}
              </span>
              <Typography variant="caption" tone="muted">
                {formatBytes(attachment.size_bytes, locale)} · {attachment.uploaded_by.display_name}{" "}
                · <RelativeTime value={attachment.uploaded_at} />
              </Typography>
            </div>

            <Button
              variant="outline"
              tone="neutral"
              size="sm"
              startIcon={<Icon icon={Download} size="sm" />}
              disabled={scanStatus !== "CLEAN" || download.isPending}
              onClick={() => void handleDownload(attachment)}
            >
              {t("attachment.download")}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
